import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  X, CheckCircle, AlertTriangle, Mail, Edit, Ban, UserCheck, Trash2, CreditCard, User, Shield
} from "lucide-react";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/badge";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { fetchUserCreditBalance, createCredit } from "../../../redux/Slices/creditSlice";
import { escrowAPI, EscrowCreateDto } from "../../../services/escrowAPI";
import { getAllCurrencies } from "../../../redux/Actions/AuthAction";
import { useCurrencyDetection, getCachedCurrencyPreference, setCachedCurrencyPreference } from "../../../lib/currencyUtils";

interface ProfessionalDetailModalProps {
  professional: any;
  onClose: () => void;
  onEditUser: (professional: any) => void;
  onSuspendUser: (userId: string) => void;
  onActivateUser: (userId: string) => void;
  onDeleteUser: (userId: string) => void;
  isInternational?: boolean;
}

const ProfessionalDetailModal: React.FC<ProfessionalDetailModalProps> = ({
  professional,
  onClose,
  onEditUser,
  onSuspendUser,
  onActivateUser,
  onDeleteUser,
  isInternational = false
}) => {
  const dispatch = useAppDispatch();
  const { currencies } = useAppSelector((state: any) => state.auth);

  // Credit management state
  const [showAddCredit, setShowAddCredit] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [userCredits, setUserCredits] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState(null);
  const [addingCredits, setAddingCredits] = useState(false);

  // Escrow management state
  const [showAddEscrow, setShowAddEscrow] = useState(false);
  const [escrowAmount, setEscrowAmount] = useState("");
  const [escrowNotes, setEscrowNotes] = useState("");
  const [userEscrowBalance, setUserEscrowBalance] = useState(null);
  const [loadingEscrowBalance, setLoadingEscrowBalance] = useState(false);
  const [loadedEscrowUserId, setLoadedEscrowUserId] = useState(null);
  const [addingEscrow, setAddingEscrow] = useState(false);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState("1"); // Default to USD
  const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);

  // Credit management functions
  const handleLoadUserCredits = useCallback(async (userId: string) => {
    // Only load if we haven't loaded credits for this user yet
    if (loadedUserId === userId && userCredits) {
      return;
    }

    setLoadingCredits(true);
    try {
      const result = await dispatch(fetchUserCreditBalance(userId));
      setUserCredits(result.payload);
      setLoadedUserId(userId);
    } catch (error) {
      console.error("Failed to load user credits:", error);
    } finally {
      setLoadingCredits(false);
    }
  }, [dispatch, loadedUserId, userCredits]);

  // Separate function to refresh credits after adding new ones
  const refreshUserCredits = useCallback(async (userId: string) => {
    setLoadingCredits(true);
    try {
      const result = await dispatch(fetchUserCreditBalance(userId));
      setUserCredits(result.payload);
      setLoadedUserId(userId);
    } catch (error) {
      console.error("Failed to refresh user credits:", error);
    } finally {
      setLoadingCredits(false);
    }
  }, [dispatch]);

  const handleAddCredits = useCallback(async (userId: string) => {
    if (!creditAmount || isNaN(Number(creditAmount)) || Number(creditAmount) <= 0) {
      alert("Please enter a valid credit amount");
      return;
    }

    setAddingCredits(true);
    try {
      const creditData = {
        status: { id: 1 }, // Available status
        credit: Number(creditAmount),
        user: { id: userId },
        escrow: null, // No escrow for admin-added credits
      };

      await dispatch(createCredit(creditData));

      // Show success message first
      alert(`${Number(creditAmount)} credits added successfully! Updating display...`);

      // Refresh user credits with the new data
      await refreshUserCredits(userId);

      // Reset form
      setCreditAmount("");
      setShowAddCredit(false);

    } catch (error) {
      console.error("Failed to add credits:", error);
      alert("Failed to add credits. Please try again.");
    } finally {
      setAddingCredits(false);
    }
  }, [creditAmount, dispatch, refreshUserCredits]);

  // Escrow management functions
  const handleLoadUserEscrowBalance = useCallback(async (userId: string) => {
    // Only load if we haven't loaded escrow balance for this user yet
    if (loadedEscrowUserId === userId && userEscrowBalance) {
      return;
    }

    setLoadingEscrowBalance(true);
    try {
      const result = await escrowAPI.getUserEscrowBalance(userId);
      setUserEscrowBalance(result);
      setLoadedEscrowUserId(userId);
    } catch (error) {
      console.error("Failed to load user escrow balance:", error);
    } finally {
      setLoadingEscrowBalance(false);
    }
  }, [loadedEscrowUserId, userEscrowBalance]);

  // Separate function to refresh escrow balance after adding new escrow
  const refreshUserEscrowBalance = useCallback(async (userId: string) => {
    setLoadingEscrowBalance(true);
    try {
      const result = await escrowAPI.getUserEscrowBalance(userId);
      setUserEscrowBalance(result);
      setLoadedEscrowUserId(userId);
    } catch (error) {
      console.error("Failed to refresh user escrow balance:", error);
    } finally {
      setLoadingEscrowBalance(false);
    }
  }, []);

  const handleAddEscrow = useCallback(async (userId: string) => {
    if (!escrowAmount || isNaN(Number(escrowAmount)) || Number(escrowAmount) <= 0) {
      alert("Please enter a valid escrow amount");
      return;
    }

    setAddingEscrow(true);
    try {
      const escrowData: EscrowCreateDto = {
        amount: Number(escrowAmount),
        withdrawlAmount: Number(escrowAmount),
        totalAmount: Number(escrowAmount),
        platformFee: 0,
        currency: selectedCurrencyId, // Use the selected currency ID
        notes: escrowNotes || `Admin added escrow for ${professional.userType}`,
        // Set client or professional ID based on user role
        ...(professional.userType === "client"
          ? { clientId: userId }
          : { professionalId: userId }
        ),
      };

      const escrow = await escrowAPI.createEscrow(escrowData);
      await escrowAPI.updateEscrow(escrow.id, {
        status: "user_funded"
      });
      // Show success message first
      alert(`${Number(escrowAmount)} escrow amount added successfully! Updating display...`);

      // Refresh user escrow balance with the new data
      await refreshUserEscrowBalance(userId);

      // Reset form
      setEscrowAmount("");
      setEscrowNotes("");
      setShowAddEscrow(false);

    } catch (error) {
      console.error("Failed to add escrow:", error);
      alert("Failed to add escrow. Please try again.");
    } finally {
      setAddingEscrow(false);
    }
  }, [escrowAmount, escrowNotes, professional.userType, refreshUserEscrowBalance, selectedCurrencyId]);

  // Load currencies when modal opens
  useEffect(() => {
    if (professional?.id) {
      // Load currencies if not already loaded
      if (!currencies || currencies.length === 0) {
        dispatch(getAllCurrencies({ isShowLoader: false }));
      }
    }
  }, [professional?.id, dispatch, currencies]);

  // Currency detection effect
  useEffect(() => {
    const detectAndSetCurrency = async () => {
      if (!currencies || currencies.length === 0 || !professional?.id) return;

      setIsDetectingCurrency(true);
      try {
        // First check for cached preference
        const cachedCurrencyId = getCachedCurrencyPreference();
        if (cachedCurrencyId) {
          console.log("Using cached currency preference:", cachedCurrencyId);
          setSelectedCurrencyId(cachedCurrencyId);
          setIsDetectingCurrency(false);
          return;
        }

        // Detect currency based on user location
        const detectedCurrencyId = await useCurrencyDetection(
          currencies,
          (currencyId, source) => {
            console.log(`Currency detected via ${source}:`, currencyId);
            setCachedCurrencyPreference(currencyId);
          }
        );

        setSelectedCurrencyId(detectedCurrencyId);
        console.log("Currency detection completed:", detectedCurrencyId);
      } catch (error) {
        console.error("Error detecting currency:", error);
        // Fallback to USD
        const usdCurrency = currencies.find((c: any) => c.initial === "USD");
        if (usdCurrency) {
          setSelectedCurrencyId(usdCurrency.value);
        }
      } finally {
        setIsDetectingCurrency(false);
      }
    };

    detectAndSetCurrency();
  }, [currencies, professional?.id]);

  // Load credits and escrow balance when modal opens
  useEffect(() => {
    if (professional?.id) {
      handleLoadUserCredits(professional.id);
      handleLoadUserEscrowBalance(professional.id);
    }
  }, [professional?.id, handleLoadUserCredits, handleLoadUserEscrowBalance]);

  // Reset state when modal closes
  const handleClose = () => {
    setShowAddCredit(false);
    setCreditAmount("");
    setUserCredits(null);
    setLoadedUserId(null);
    setAddingCredits(false);
    setShowAddEscrow(false);
    setEscrowAmount("");
    setEscrowNotes("");
    setUserEscrowBalance(null);
    setLoadedEscrowUserId(null);
    setAddingEscrow(false);
    setSelectedCurrencyId("1");
    setIsDetectingCurrency(false);
    onClose();
  };

  if (!professional) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">{professional.name}</h2>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>ID: {professional.id}</span>
                  <span>•</span>
                  <Badge variant={undefined} className={
                    professional.status === "active" ? "bg-green-100 text-green-700" :
                      professional.status === "suspended" ? "bg-red-100 text-red-700" :
                        professional.status === "pending" ? "bg-yellow-100 text-yellow-700" :
                          professional.status === "inactive" ? "bg-gray-100 text-gray-700" :
                            professional.status === "approved" ? "bg-blue-100 text-blue-700" :
                              professional.status === "rejected" ? "bg-red-100 text-red-700" :
                                professional.status === "completed" ? "bg-green-100 text-green-700" :
                                  professional.status === "cancelled" ? "bg-gray-100 text-gray-700" :
                                    professional.status === "expired" ? "bg-orange-100 text-orange-700" :
                                      professional.status === "deleted" ? "bg-red-200 text-red-800" :
                                        "bg-blue-100 text-blue-700"
                  }>
                    {professional.status.charAt(0).toUpperCase() + professional.status.slice(1)}
                  </Badge>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium">
              {professional.userType === "client" ? "Client Information" :
                professional.userType === "professional" ? "Professional Information" :
                  "User Information"}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="font-medium">{professional.email}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Phone</span>
                <p className="font-medium">{professional.phone}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Location</span>
                <p className="font-medium">{professional.location}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Registration Date</span>
                <p className="font-medium">{professional.registeredDate}</p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="space-y-4">
            <h3 className="font-medium">
              {professional.userType === "client" ? "Client Details" : "Service Details"}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">
                  {professional.userType === "client" ? "User Type" : "Profession"}
                </span>
                <p className="text-xl font-semibold">{professional.profession}</p>
              </div>
              {isInternational ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Time Zone</span>
                  <p className="text-xl font-semibold">{professional.timeZone}</p>
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-500">Service Area</span>
                  <p className="text-xl font-semibold">{professional.serviceArea}</p>
                </div>
              )}
              <div className="p-4 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">
                  {professional.userType === "client" ? "Jobs Posted" : "Completed Jobs"}
                </span>
                <p className="text-xl font-semibold">{professional.completedJobs}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">
                  {professional.userType === "client" ? "Total Spent" : "Total Earnings"}
                </span>
                <p className="text-xl font-semibold">
                  {professional.userType === "client" ? professional.totalSpent : professional.totalEarnings}
                </p>
              </div>
            </div>
          </div>

          {/* International-specific sections */}
          {isInternational && (
            <>
              {/* Languages and Service Regions */}
              <div className="space-y-4">
                <h3 className="font-medium">Languages & Service Areas</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div>
                    <span className="text-sm text-gray-500">Languages</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {professional.languages.map((language, index) => (
                        <Badge variant={undefined} key={index} className="bg-purple-100 text-purple-700">
                          {language}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Service Regions</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {professional.serviceRegions.map((region, index) => (
                        <Badge variant={undefined} key={index} className="bg-blue-100 text-blue-700">
                          {region}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* International Verification Status */}
              <div className="space-y-4">
                <h3 className="font-medium">International Verification Status</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-5 w-5 ${professional.internationalVerification.businessLicense ? "text-green-600" : "text-gray-400"}`} />
                      <span>Business License</span>
                    </div>
                    <Badge variant={undefined} className={professional.internationalVerification.businessLicense ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {professional.internationalVerification.businessLicense ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-5 w-5 ${professional.internationalVerification.taxCompliance ? "text-green-600" : "text-gray-400"}`} />
                      <span>Tax Compliance</span>
                    </div>
                    <Badge variant={undefined} className={professional.internationalVerification.taxCompliance ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {professional.internationalVerification.taxCompliance ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-5 w-5 ${professional.internationalVerification.backgroundCheck ? "text-green-600" : "text-gray-400"}`} />
                      <span>Background Check</span>
                    </div>
                    <Badge variant={undefined} className={professional.internationalVerification.backgroundCheck ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {professional.internationalVerification.backgroundCheck ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className={`h-5 w-5 ${professional.internationalVerification.insuranceCoverage ? "text-green-600" : "text-gray-400"}`} />
                      <span>Insurance Coverage</span>
                    </div>
                    <Badge variant={undefined} className={professional.internationalVerification.insuranceCoverage ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {professional.internationalVerification.insuranceCoverage ? "Verified" : "Pending"}
                    </Badge>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Local-specific sections */}
          {!isInternational && (
            <>
              {/* Expertise - Only show for professionals */}
              {professional.userType === "professional" && professional.expertise && professional.expertise.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-medium">Expertise</h3>
                  <div className="flex flex-wrap gap-2">
                    {professional.expertise.map((skill, index) => (
                      <Badge variant={undefined} key={index} className="bg-blue-100 text-blue-700">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Verification Status */}
              <div className="space-y-4">
                <h3 className="font-medium">Verification Status</h3>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {professional.verified ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      )}
                      <div>
                        <p className="font-medium">
                          {professional.verified ?
                            (professional.userType === "client" ? "Verified Client" : "Verified Professional") :
                            "Verification Pending"
                          }
                        </p>
                        <p className="text-sm text-gray-500">
                          {professional.verified
                            ? (professional.userType === "client" ?
                              "Client account verification completed" :
                              "All required verifications completed")
                            : "Background check and documentation pending"
                          }
                        </p>
                      </div>
                    </div>
                    {!professional.verified && (
                      <Button size="sm">Review Documents</Button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Credit Management Section */}
          {professional.userType === "professional" &&
            <div className="space-y-4">
              <h3 className="font-medium">Credit Management</h3>
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                {/* Current Credits Display */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Current Credits</p>
                      <p className="text-sm text-gray-500">
                        {loadingCredits ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            {userCredits ? "Updating..." : "Loading..."}
                          </span>
                        ) : (
                          <div className="space-y-1">
                            <span className="flex items-center gap-2">
                              <span className="font-semibold text-blue-600">
                                {userCredits?.availableCredits}
                              </span>
                              <span>credits available</span>
                            </span>
                          </div>
                        )}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowAddCredit(!showAddCredit)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Add Credits
                  </Button>
                </div>

                {/* Add Credits Form */}
                {showAddCredit && (
                  <div className="border-t pt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Credit Amount
                      </label>
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        placeholder="Enter credit amount"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAddCredits(professional.id)}
                        disabled={!creditAmount || isNaN(Number(creditAmount)) || Number(creditAmount) <= 0 || addingCredits}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {addingCredits ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Adding...
                          </span>
                        ) : (
                          "Save Credits"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowAddCredit(false);
                          setCreditAmount("");
                        }}
                        disabled={addingCredits}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          }


          {/* Escrow Management Section */}
          <div className="space-y-4">
            <h3 className="font-medium">Wallet Management</h3>
            <div className="p-4 bg-gray-50 rounded-lg space-y-4">
              {/* Current Escrow Balance Display */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Shield className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Wallet Balance</p>
                    <p className="text-sm text-gray-500">
                      {loadingEscrowBalance ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                          {userEscrowBalance ? "Updating..." : "Loading..."}
                        </span>
                      ) : (
                        <div className="space-y-1">
                          <span className="flex items-center gap-2">
                            <span className="font-semibold text-green-600">
                              ${userEscrowBalance?.availableBalance || 0}
                            </span>
                            <span>available balance</span>
                          </span>
                          {userEscrowBalance?.escrowHeld > 0 && (
                            <span className="flex items-center gap-2 text-sm text-orange-600">
                              <span className="font-semibold">
                                ${userEscrowBalance.escrowHeld}
                              </span>
                              <span>held in escrow</span>
                            </span>
                          )}
                        </div>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowAddEscrow(!showAddEscrow)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Add Fund
                </Button>
              </div>

              {/* Add Escrow Form */}
              {showAddEscrow && (
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Amount
                    </label>
                    <input
                      type="number"
                      value={escrowAmount}
                      onChange={(e) => setEscrowAmount(e.target.value)}
                      placeholder="Enter escrow amount"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      min="1"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                      {isDetectingCurrency && (
                        <span className="ml-2 text-xs text-green-600">
                          🔄 Detecting based on location...
                        </span>
                      )}
                    </label>
                    <select
                      value={selectedCurrencyId}
                      onChange={(e) => {
                        setSelectedCurrencyId(e.target.value);
                        // Update cache when user manually changes currency
                        setCachedCurrencyPreference(e.target.value);
                      }}
                      disabled={isDetectingCurrency}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {currencies && currencies.length > 0 ? (
                        currencies.map((currency: any) => (
                          <option key={currency.value} value={currency.value}>
                            {currency.initial} - {currency.label}
                          </option>
                        ))
                      ) : (
                        <option value="1">USD - US Dollar</option>
                      )}
                    </select>
                    {isDetectingCurrency && (
                      <p className="mt-1 text-xs text-gray-500">
                        Automatically detecting preferred currency based on location...
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={escrowNotes}
                      onChange={(e) => setEscrowNotes(e.target.value)}
                      placeholder="Add notes for this escrow record"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleAddEscrow(professional.id)}
                      disabled={!escrowAmount || isNaN(Number(escrowAmount)) || Number(escrowAmount) <= 0 || addingEscrow}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {addingEscrow ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Adding...
                        </span>
                      ) : (
                        "Save Fund"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddEscrow(false);
                        setEscrowAmount("");
                        setEscrowNotes("");
                      }}
                      disabled={addingEscrow}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Admin Actions */}
          <div className="space-y-4">
            <h3 className="font-medium">Admin Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send Email
              </Button>
              <Button variant="outline" className="flex items-center gap-2" onClick={() => onEditUser(professional)}>
                <Edit className="h-4 w-4" />
                Edit Details
              </Button>
              {professional.status === "active" ? (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-yellow-600"
                  onClick={() => onSuspendUser(professional.id)}
                >
                  <Ban className="h-4 w-4" />
                  Suspend Account
                </Button>
              ) : professional.status === "deleted" ? (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-green-600"
                  onClick={() => onActivateUser(professional.id)}
                >
                  <UserCheck className="h-4 w-4" />
                  Reactivate Account
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-green-600"
                  onClick={() => onActivateUser(professional.id)}
                >
                  <UserCheck className="h-4 w-4" />
                  Activate Account
                </Button>
              )}
              {professional.status !== "deleted" && (
                <Button
                  variant="outline"
                  className="flex items-center gap-2 text-red-600"
                  onClick={() => onDeleteUser(professional.id)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Account
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfessionalDetailModal;
